import React from 'react';
import { fromLonLat } from 'ol/proj';
import { Overlay } from 'ol';
import { settingsConfig } from 'utils/commonFunc';

export const OverlayContainer = React.forwardRef((props, ref) => {
    const { map } = props
    const [overlayContainers, setOverlayContainers] = React.useState([])

    React.useEffect(() => {
        if (!map)
            return

        overlayContainers.map(obj => {
            if (!obj.overlay) {
                const overlay =new Overlay({
                    element: obj.ref.current,
                    positioning: 'bottom-left',
                    offset: [0, -10],
                });        
                map.addOverlay(overlay);
                obj.overlay = overlay;
            }

            if (obj.overlay)
            {
                positioningChange(obj)                
                obj.overlay.element.style.zIndex = obj.zIndex;
            }

            return obj;
        })
    }, [map, overlayContainers]);

    React.useImperativeHandle(ref, () => ({
        Add,
        Remove,
        RemoveAll,
        RemoveExcept,
        Update,
        AddOrUpdate,
    }))

    const Add = (id, coordinate, positioning, children, zIndex) => {
        setOverlayContainers((containers) => {
            const container = containers.find(p => !p.coordinate)
            if (container) {
                container.id = id
                container.coordinate = coordinate
                container.positioning = positioning
                container.children = children
                container.zIndex = zIndex
                return [...containers]
            }
            else {
                return [...containers, {
                    ref: React.createRef(),
                    id,
                    coordinate,
                    positioning,
                    children,
                    zIndex,
                }]
            }
        });
    }

    const Remove = (id) => {
        setOverlayContainers((containers) => {
            const container = containers.find(obj => obj.id === id);
            if (container) {
                container.id = undefined;
                container.coordinate = undefined;
                container.positioning = undefined;
                container.children = undefined;
                container.overlay?.setPosition(undefined);
            }
            return [...containers]
        });
    }

    const RemoveAll = () => {
        setOverlayContainers((containers) => {
            containers.forEach(container => {
                container.id = undefined;
                container.coordinate = undefined;
                container.positioning = undefined;
                container.children = undefined;
                container.overlay?.setPosition(undefined);
            })
            return [...containers]
        });
    }

    const RemoveExcept = (ids) => {
        setOverlayContainers((containers) => {            
            containers.forEach(container => {
                const found = ids.find(id => id === container.id);
                if(!found) {
                    container.id = undefined;
                    container.coordinate = undefined;
                    container.positioning = undefined;
                    container.children = undefined;
                    container.overlay?.setPosition(undefined);    
                }                
            })
            return [...containers]
        });
    }

    const Update = (id, coordinate, positioning) => {
        const container = overlayContainers.find(obj => obj.id === id);
        if (container) {
            container.coordinate = coordinate;
            container.positioning = positioning;
            positioningChange(container)
            return container
        }
    }

    const AddOrUpdate = (id, coordinate, positioning, components, updated, zIndex = 1) => {
        const container = Update(id, coordinate, positioning)

        if(updated && container?.children){
            if(updated(container.children.props)){
                container.children = components();
            }
        }

        if (!container) {
            Add(id, coordinate, positioning, components(), zIndex)
        }
    }

    const positioningChange = (container) => {
        if(!container.overlay)
            return;

        const coordinate = container.coordinate
        const positioning = container.positioning
        const gap = settingsConfig().mapPopupGap ?? 20

        if(coordinate)
            container.overlay.setPosition(fromLonLat(coordinate));
        if(positioning)
        {
            container.overlay.setPositioning(positioning);
            switch(positioning){
                case "bottom-left":
                    container.overlay.setOffset([gap, -gap]); 
                    break;
                case "bottom-center":
                    container.overlay.setOffset([0, -gap]); 
                    break;
                case "bottom-right":
                    container.overlay.setOffset([-gap, -gap]); 
                    break;                
                case "top-left":
                    container.overlay.setOffset([gap, gap]); 
                    break;
                case "top-center":
                    container.overlay.setOffset([0, gap]); 
                    break;
                case "top-right":
                    container.overlay.setOffset([-gap, gap]); 
                    break;                              
                case "center-left":
                    container.overlay.setOffset([gap, 0]); 
                    break;
                case "center-center":
                    container.overlay.setOffset([0, 0]); 
                    break;                    
                case "center-right":
                    container.overlay.setOffset([-gap, 0]); 
                    break;
                default:
                    container.overlay.setOffset([0, 0]); 
                    break;
            } 
        }              
    }

    return (
        <React.Fragment>
            {overlayContainers.map((element, index) => (
                <div key={index} ref={element.ref}>
                    {element.children}
                </div>
            ))}
        </React.Fragment>
    )
})